import { Button, ButtonSize, isServerRendering, ScrollLock } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { PictureProfile } from '../../../../ResizedImage/PictureProfile.csharp';
import { getTranslation, keys } from '../../../../Resources/Translations';
import { ApplicationInsights } from '../../ApplicationInsights';
import { ResponsivePictureViewModel } from '../../ResponsivePicture/ResponsivePictureViewModel.csharp';
import { ViagraFormGenderSelect } from './ViagraFormGenderSelect';
import { ViagraFormHeader } from './ViagraFormHeader';
import { ViagraFormQuestion } from './ViagraFormQuestion';
import { Question, QuestionState } from './ViagraFormTypings';

interface ViagraFormProps {
    readonly isVisible: boolean;
    readonly addToCart: () => void;
    readonly closeForm: () => void;
    readonly mainName: string;
    readonly additionalName: string;
    readonly price: number;
    readonly image: ResponsivePictureViewModel;
    readonly profile: PictureProfile;
}

interface ViagraFormState {
    readonly questions: Question[];
    readonly isMan?: boolean;
    readonly areAllChecked: boolean;
    readonly areCustomerAllowedToBuy: boolean;
    readonly isContactWithPharmacistChecked: boolean;
    readonly showErrors: boolean;
    readonly wasChatRejected: boolean;
    readonly isZenDeskEventAdded: boolean;
    readonly isChatOpen: boolean;
    readonly isCheckboxChecked: boolean;
    readonly isZenDeskUnavailable: boolean;
    readonly isZendeskChatServed: boolean;
    readonly isZendeskChatMessages: boolean;
    readonly isPredefinedMessageSent: boolean;
}

// eslint-disable-next-line
declare let zE: any;
// eslint-disable-next-line
declare let zECustomSettings: any;

export class ViagraForm extends React.Component<ViagraFormProps, ViagraFormState> {
    constructor(props: ViagraFormProps) {
        super(props);

        this.state = {
            questions: this.getQuestions(),
            areAllChecked: false,
            areCustomerAllowedToBuy: false,
            isContactWithPharmacistChecked: true,
            showErrors: false,
            wasChatRejected: false,
            isZenDeskEventAdded: false,
            isChatOpen: false,
            isCheckboxChecked: false,
            isZenDeskUnavailable: false,
            isZendeskChatServed: false,
            isZendeskChatMessages: false,
            isPredefinedMessageSent: false
        };
    }

    componentDidMount(): void {
        if (typeof window === undefined || !zECustomSettings?.useCustomizedZendeskForViagraForm) return;

        const isZendeskChatMessages = sessionStorage?.getItem('isZendeskChatMessages');
        const isZendeskChatServed = sessionStorage?.getItem('isZendeskChatServed');

        if (isZendeskChatMessages) {
            this.setState({
                isZendeskChatMessages: true
            });
        }
        if (isZendeskChatServed) {
            this.setState({
                isZendeskChatServed: true
            });
        }

        if (isZendeskChatMessages && isZendeskChatServed) {
            this.setState({
                wasChatRejected: true
            });
        }

        if (isZendeskChatMessages || isZendeskChatServed) {
            this.checkStatus();
        }
    }

    componentDidUpdate(prevProps: ViagraFormProps) {
        if (prevProps.isVisible && this.props.isVisible && !this.state.isZenDeskEventAdded && typeof zE !== 'undefined') {
            zE('webWidget:on', 'close', this.onChatClose);

            if (zECustomSettings?.useCustomizedZendeskForViagraForm) {
                zE('webWidget:on', 'userEvent', (userEvent: unknown) => {
                    let action: string | undefined;
                    if (this.hasActionAndEvent(userEvent)) {
                        action = userEvent.action.toLowerCase();
                    }

                    if (action === 'chat served by operator') {
                        this.setState({
                            isZendeskChatServed: true
                        });
                    }

                    if (action === 'web widget opened') {
                        zE('webWidget', 'updateSettings', {
                            webWidget: {
                                contactForm: {
                                    suppress: false
                                }
                            }
                        });
                    }
                });

                zE('webWidget:on', 'chat:unreadMessages', () => {
                    if (this.state.isZendeskChatServed) {
                        this.setState({
                            isZendeskChatMessages: true
                        });
                    }
                });

                zE('webWidget:on', 'chat:end', () => {
                    if (this.state.isZendeskChatServed && this.state.isZendeskChatMessages) {
                        if (this.state.areAllChecked && this.state.isCheckboxChecked && this.state.areCustomerAllowedToBuy) {
                            this.allowToBuyAfterChatEnds();
                        } else {
                            this.setState({ isChatOpen: false });
                        }
                    }
                });
            }

            this.setState({ isZenDeskEventAdded: true });
        }
    }

    render() {
        const { image, mainName, additionalName, price, profile, isVisible } = this.props;
        const { questions, isMan, areAllChecked, isContactWithPharmacistChecked, showErrors, wasChatRejected, isCheckboxChecked, isZenDeskUnavailable } =
            this.state;

        if (isServerRendering()) {
            return <></>;
        }

        return ReactDOM.createPortal(
            <div className={classNames('ViagraForm', !isMan && 'ViagraForm--Collapsed', isVisible && 'ViagraForm--Visible')} onClick={this.props.closeForm}>
                <ScrollLock isLocked={isVisible} removeScroll={isVisible && isMan} bodyClassName="ScrollLock--HelpOnTop" />

                <div className={classNames('ViagraForm__Content', isMan && 'ViagraForm__Content--WithQuestions')} onClick={(e) => e.stopPropagation()}>
                    <button className="ViagraForm__CloseButton" onClick={this.props.closeForm}>
                        <span className="visuallyHidden">Close form</span>
                    </button>

                    <div className="ViagraForm__ContentInner">
                        <ViagraFormHeader profile={profile} image={image} mainName={mainName} additionalName={additionalName} price={price} />

                        <ViagraFormGenderSelect isMan={isMan} selectMan={this.selectMan} selectWoman={this.selectWoman} />

                        {isMan && (
                            <div className="ViagraForm__Questions">
                                <div className="ViagraForm__QuestionLine">
                                    <div className="ViagraForm__Question">
                                        <span className="ViagraForm__QuestionText ViagraForm__QuestionText--NoMargin">
                                            <button onClick={this.openChat} className="ViagraForm__ContactLink">
                                                {getTranslation(keys['viagraForm.contactUs'])}
                                            </button>
                                            {getTranslation(keys['viagraForm.discuss'])}
                                        </span>
                                        <span className="ViagraForm__AnswerLabel">{getTranslation(keys['viagraForm.yes'])}</span>
                                        <span className="ViagraForm__AnswerLabel">{getTranslation(keys['viagraForm.no'])}</span>
                                    </div>
                                </div>

                                {questions.map((question, i) => (
                                    <ViagraFormQuestion
                                        key={i}
                                        question={question}
                                        showErrors={showErrors}
                                        questionIndex={i}
                                        setQuestionAnswer={this.setQuestionAnswer}
                                    />
                                ))}

                                <div className="ViagraForm__QuestionLine">
                                    <div className="ViagraForm__Question">
                                        <div className="ViagraForm__QuestionBox">
                                            <span className="ViagraForm__QuestionNumber">{questions.length + 1}</span>
                                            <span className="ViagraForm__QuestionText ViagraForm__QuestionText--Bold">
                                                {getTranslation(keys['viagraForm.guidance'])}
                                            </span>
                                            <span className="ViagraForm__QuestionText">{getTranslation(keys['viagraForm.guidanceSubText'])}</span>
                                        </div>
                                        <label
                                            className={classNames('ViagraForm__QuestionButton', this.isDisabled() && 'ViagraForm__QuestionButton--Disabled')}
                                        >
                                            <input
                                                type="radio"
                                                name={`question-guidance`}
                                                onChange={() => this.setState({ isContactWithPharmacistChecked: true })}
                                                checked={isContactWithPharmacistChecked}
                                                disabled={this.isDisabled()}
                                            />
                                            <span className="ViagraForm__QuestionLabel">{getTranslation(keys['viagraForm.yes'])}</span>
                                        </label>
                                        <label
                                            className={classNames('ViagraForm__QuestionButton', this.isDisabled() && 'ViagraForm__QuestionButton--Disabled')}
                                        >
                                            <input
                                                type="radio"
                                                name={`question-guidance`}
                                                onChange={() => this.setState({ isContactWithPharmacistChecked: false })}
                                                checked={!isContactWithPharmacistChecked}
                                                disabled={this.isDisabled()}
                                            />
                                            <span className="ViagraForm__QuestionLabel">{getTranslation(keys['viagraForm.no'])}</span>
                                        </label>
                                    </div>
                                </div>

                                <div
                                    className={classNames(
                                        'CustomCheckbox',
                                        'ViagraForm__Checkbox',
                                        showErrors && !isCheckboxChecked && 'ViagraForm__Checkbox--Error'
                                    )}
                                >
                                    <input
                                        type="checkbox"
                                        id="ViagraFormCheckbox"
                                        checked={isCheckboxChecked}
                                        onChange={() =>
                                            this.setState((prevState) => {
                                                return {
                                                    ...prevState,
                                                    isCheckboxChecked: !prevState.isCheckboxChecked
                                                };
                                            }, this.checkStatus)
                                        }
                                        required
                                    />
                                    <label htmlFor="ViagraFormCheckbox">{getTranslation(keys['viagraForm.checkbox'])}</label>
                                </div>
                            </div>
                        )}

                        {showErrors && areAllChecked && isContactWithPharmacistChecked && wasChatRejected && (
                            <span className="ViagraForm__Message ViagraForm__Message--Center">{getTranslation(keys['viagraForm.contactWithDoctor'])}</span>
                        )}

                        {showErrors && isZenDeskUnavailable && (
                            <span className="ViagraForm__Message ViagraForm__Message--Center">{getTranslation(keys['viagraForm.zenDeskUnavailable'])}</span>
                        )}

                        {showErrors && (!areAllChecked || !isCheckboxChecked) && (
                            <span className="ViagraForm__Message ViagraForm__Message--Center">{getTranslation(keys['viagraForm.answerAllQuestions'])}</span>
                        )}

                        {isMan === false && (
                            <span className="ViagraForm__Message ViagraForm__Message--Right">{getTranslation(keys['viagraForm.onlyForMan'])}</span>
                        )}

                        {isMan && (
                            <div className="ViagraForm__ButtonWrapper">
                                <Button
                                    size={ButtonSize.Medium}
                                    className="ViagraForm__BuyButton"
                                    onClick={this.handleAddToCart}
                                    disabled={this.shouldBeBuyButtonDisabled()}
                                >
                                    {this.state.isContactWithPharmacistChecked ? (
                                        <span>{getTranslation(keys['viagraForm.continue'])}</span>
                                    ) : (
                                        <span>{getTranslation(keys['viagraForm.addToCart'])}</span>
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>,
            document.body
        );
    }

    private shouldBeBuyButtonDisabled = (): boolean => {
        const { showErrors, areAllChecked, wasChatRejected, areCustomerAllowedToBuy, isChatOpen } = this.state;

        if (showErrors) {
            if (areAllChecked && wasChatRejected && areCustomerAllowedToBuy) return false;
            if (!areAllChecked) return true;
            if (!areCustomerAllowedToBuy) return true;
            if (isChatOpen) return true;
        }

        return false;
    };

    private selectMan = () => {
        this.setState({ isMan: true });
    };

    private resetForm = (isMan: boolean | undefined, callback?: () => void) => {
        this.setState(
            {
                isMan: isMan,
                questions: this.getQuestions(),
                areAllChecked: false,
                wasChatRejected: false,
                isContactWithPharmacistChecked: true,
                showErrors: false,
                areCustomerAllowedToBuy: false,
                isCheckboxChecked: false
            },
            callback
        );
    };

    private selectWoman = () => {
        this.resetForm(false, this.closeChat);
    };

    private isDisabled = (): boolean => !this.state.wasChatRejected;

    private handleAddToCart = () => {
        const { areAllChecked, areCustomerAllowedToBuy, wasChatRejected, isContactWithPharmacistChecked } = this.state;
        if (areAllChecked && areCustomerAllowedToBuy && wasChatRejected && !isContactWithPharmacistChecked) {
            sessionStorage?.removeItem('isZendeskChatMessages');
            sessionStorage?.removeItem('isZendeskChatServed');
            this.props.addToCart();
            this.props.closeForm();
            return this.resetForm(undefined);
        } else if (areAllChecked && areCustomerAllowedToBuy && isContactWithPharmacistChecked) {
            this.setState({ showErrors: true });
            return this.openChat();
        }

        return this.setState({ showErrors: true });
    };

    private hasActionAndEvent = (userEvent: unknown): userEvent is { action: string } => {
        return typeof userEvent === 'object' && userEvent !== null && 'action' in userEvent && typeof (userEvent as { action: unknown }).action === 'string';
    };

    private allowToBuyAfterChatEnds = () => {
        this.setState({
            isContactWithPharmacistChecked: false,
            wasChatRejected: true,
            isChatOpen: false,
            isZendeskChatServed: false,
            isZendeskChatMessages: false
        });
    };

    private loadZendeskForViagra = () => {
        zE('webWidget', 'updateSettings', {
            webWidget: {
                contactForm: {
                    suppress: true
                },
                chat: {
                    departments: {
                        select: 360001299260
                    }
                }
            }
        });
        zE('webWidget', 'chat:addTags', ['viagra']);
        zE('webWidget', 'show');
        zE('webWidget', 'open');
        if (this.state.isPredefinedMessageSent) return;

        if (zECustomSettings.predefinedMessage) {
            zE('webWidget', 'chat:send', zECustomSettings.predefinedMessage);
            this.setState({
                isPredefinedMessageSent: true
            });
        }
    };

    private openChat = () => {
        try {
            if (!zECustomSettings?.useCustomizedZendeskForViagraForm) {
                zE('webWidget', 'show');
                zE('webWidget', 'open');
            } else {
                this.loadZendeskForViagra();
            }
            this.setState({ isChatOpen: true, isZenDeskUnavailable: false });
        } catch (e) {
            ApplicationInsights.TrackException('ZenDesk chat unavailable in Viagra form');
            this.setState({ isZenDeskUnavailable: true, isChatOpen: false });
        }
    };

    private closeChat = () => {
        if (typeof zE !== 'undefined') {
            zE('webWidget', 'close');
        }
    };

    private onChatClose = () => {
        if (typeof zE !== 'undefined') {
            zE('webWidget', 'chat:removeTags', ['viagra']);
        }

        if (!zECustomSettings?.useCustomizedZendeskForViagraForm) {
            return this.allowToBuyAfterChatEnds();
        } else if (this.state.isZendeskChatServed && this.state.isZendeskChatMessages) {
            if (this.state.areAllChecked && this.state.isCheckboxChecked && this.state.areCustomerAllowedToBuy) {
                return this.allowToBuyAfterChatEnds();
            }
        }

        this.setState({ isChatOpen: false });
    };

    private setQuestionAnswer = (answer: QuestionState, questionIndex: number, subQuestionIndex?: number) => {
        this.setState((state) => {
            if (subQuestionIndex !== undefined && state.questions[questionIndex].subQuestions !== undefined) {
                // eslint-disable-next-line
                state.questions[questionIndex].subQuestions![subQuestionIndex].value = answer;
            } else {
                state.questions[questionIndex].value = answer;
            }

            return state;
        }, this.checkStatus);
    };

    private checkStatus = () => {
        let areCustomerAllowed = true;
        let areAllChecked = this.state.isCheckboxChecked;

        this.state.questions.map((question) => {
            if (question.value === QuestionState.WithSubQuestions && question.subQuestions) {
                question.subQuestions.map((subQuestion) => {
                    if (subQuestion.value !== QuestionState.No) {
                        areCustomerAllowed = false;
                    }

                    if (subQuestion.value === QuestionState.NotAnswered) {
                        areAllChecked = false;
                    }
                });
            } else {
                if (question.value !== QuestionState.No) {
                    areCustomerAllowed = false;
                }

                if (question.value === QuestionState.NotAnswered) {
                    areAllChecked = false;
                }
            }
        });

        if (areAllChecked && areCustomerAllowed) {
            this.setState({ areCustomerAllowedToBuy: true, areAllChecked: true });
        } else if (areAllChecked && !areCustomerAllowed) {
            this.setState({ areCustomerAllowedToBuy: false, areAllChecked: true });
        } else {
            this.setState({ areAllChecked: false });
        }
    };

    private getQuestions = (): Question[] => {
        return [
            {
                text: getTranslation(keys['viagraForm.anyOfFollowing']),
                value: QuestionState.WithSubQuestions,
                subQuestions: [
                    {
                        text: getTranslation(keys['viagraForm.lowBloodPressure']),
                        explanation: getTranslation(keys['viagraForm.lowBloodPressureExplanation']),
                        value: QuestionState.NotAnswered
                    },
                    {
                        text: getTranslation(keys['viagraForm.highBloodPressure']),
                        explanation: getTranslation(keys['viagraForm.highBloodPressureExplanation']),
                        value: QuestionState.NotAnswered
                    },
                    {
                        text: getTranslation(keys['viagraForm.angina']),
                        explanation: getTranslation(keys['viagraForm.anginaExplanation']),
                        value: QuestionState.NotAnswered
                    },
                    {
                        text: getTranslation(keys['viagraForm.irregularHeartbeats']),
                        explanation: getTranslation(keys['viagraForm.irregularHeartbeatsExplanation']),
                        value: QuestionState.NotAnswered
                    }
                ]
            },
            {
                text: getTranslation(keys['viagraForm.anticonvulsantMedicines']),
                explanation: getTranslation(keys['viagraForm.anticonvulsantMedicinesExplanation']),
                value: QuestionState.NotAnswered
            },
            {
                text: getTranslation(keys['viagraForm.heartAttack']),
                explanation: getTranslation(keys['viagraForm.heartAttackExplanation']),
                value: QuestionState.NotAnswered
            },
            {
                text: getTranslation(keys['viagraForm.poppers']),
                explanation: getTranslation(keys['viagraForm.poppersExplanation']),
                value: QuestionState.NotAnswered
            },
            {
                text: getTranslation(keys['viagraForm.adempas']),
                explanation: getTranslation(keys['viagraForm.adempasExplanation']),
                value: QuestionState.NotAnswered
            },
            {
                text: getTranslation(keys['viagraForm.ritonavir']),
                explanation: getTranslation(keys['viagraForm.ritonavirExplanation']),
                value: QuestionState.NotAnswered
            },
            {
                text: getTranslation(keys['viagraForm.opticDamage']),
                explanation: getTranslation(keys['viagraForm.opticDamageExplanation']),
                value: QuestionState.NotAnswered
            }
        ];
    };
}
